import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function SalesSummary() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'sales-summary', startDate, endDate],
    queryFn: () => reportsApi.getSalesSummary({ startDate, endDate }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/sales-summary', { startDate, endDate });
    } finally {
      setExporting(false);
    }
  };

  const rows =
    data?.data?.byType
      ? Object.entries(data.data.byType).map(([type, info]: [string, any]) => [
          type.charAt(0).toUpperCase() + type.slice(1),
          info.count ?? 0,
          formatCurrency(info.revenue ?? 0),
          info.count ? formatCurrency((info.revenue ?? 0) / info.count) : '$0.00',
        ])
      : [];

  return (
    <Page
      title="Sales Summary"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Sales Summary report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No sales data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={3} gap="400">
                <StatCard label="Total Orders" value={String(data.data.totalOrders ?? 0)} />
                <StatCard label="Total Revenue" value={formatCurrency(data.data.totalRevenue ?? 0)} />
                <StatCard label="Avg Order Value" value={formatCurrency(data.data.avgOrderValue ?? 0)} />
              </InlineGrid>

              <DataTable
                columnContentTypes={['text', 'numeric', 'text', 'text']}
                headings={['Type', 'Orders', 'Revenue', 'Avg Value']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
